import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FormActionButtons from "../Assets/FormActionButtons";
import ImageUpload from "../Assets/ImageUpload";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";
import { NewsItem } from "../Types/Types";

interface NewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  initialData?: NewsItem;
}

type Multi = { en: string; mk: string; sq: string };

const toMulti = (field?: { [key: string]: string }): Multi => ({
  en: field?.en || "",
  mk: field?.mk || "",
  sq: field?.sq || "",
});

const NewsModal: React.FC<NewsModalProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const { t } = useTranslation();
  const isEdit = !!initialData;
  const [activeLang, setActiveLang] = useState<FormLang>("en");
  const [title, setTitle] = useState<Multi>(toMulti(initialData?.title));
  const [content, setContent] = useState<Multi>(toMulti(initialData?.content));
  const [image, setImage] = useState<File | null>(null);

  const setField = (
    setter: React.Dispatch<React.SetStateAction<Multi>>,
    value: string
  ) => setter((prev) => ({ ...prev, [activeLang]: value }));

  const isQuillEmpty = (html: string) =>
    html.replace(/<(.|\n)*?>/g, "").replace(/&nbsp;/g, "").trim().length === 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", JSON.stringify(title));
    formData.append("content", JSON.stringify(content));
    if (image) formData.append("image", image);
    onSubmit(formData);
    onClose();
  };

  if (!isOpen) return null;
  const langSuffix = activeLang.toUpperCase();

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto font-montserrat">
        <h2 className="text-2xl font-semibold mb-6 text-brand-800 text-center">
          {t(isEdit ? "forms.editNews" : "forms.createNews")}
        </h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium mb-1">{t("forms.title")} ({langSuffix})</label>
            <input
              type="text"
              value={title[activeLang]}
              onChange={(e) => setField(setTitle, e.target.value)}
              className="w-full border border-gray-300 p-3 rounded-md"
              required={activeLang !== "sq"}
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t("forms.content")} ({langSuffix})</label>
            <ReactQuill
              key={`content-${activeLang}`}
              value={content[activeLang]}
              onChange={(value) => setField(setContent, value)}
              theme="snow"
              className="h-64"
            />
          </div>
          <br />
          <br />

          <div>
            <label className="block font-medium mb-1">{t("forms.cover")}</label>
            <ImageUpload
              onImageSelect={(file) => setImage(file)}
              initialImage={!image ? initialData?.image : undefined}
            />
          </div>

          <FormActionButtons
            edit={isEdit}
            onClose={onClose}
            onSubmit={handleSubmit}
            disabled={!title.en || !title.mk || isQuillEmpty(content.en) || isQuillEmpty(content.mk)}
          />
        </form>
      </div>
    </div>
  );
};

export default NewsModal;
